
import { Product } from '@/lib/session';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, ExternalLink, Info, AlertCircle, ThumbsDown } from 'lucide-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ProductCardProps {
  product: Product;
}

export const ProductCard = ({ product }: ProductCardProps) => {
  const hasValidLink = product.link && 
    product.link !== 'javascript:void(0)' && 
    product.link.startsWith('http') &&
    !product.link.includes('google.com/shopping');

  const formatMileage = (mileage: number) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  const formatPrice = (price: number | null | undefined) => {
    if (price == null) return 'Price unavailable';
    return `$${price.toLocaleString()}`;
  };

  const getDealerWarning = (dealerName: string) => {
    const warnings: Record<string, string> = {
      'DARCARS Lexus Of Silver Spring': 'Reported braking issues: Customer reported front-wheel vibration during braking, requiring brake disc replacement.',
    };
    return warnings[dealerName];
  };

  const isNegativeRating = (rating: number) => rating < 4.0;

  return (
    <Card className="product-card">
      {/* Main Image Section */}
      <div className="relative">
        <img
          src={product.imageUrl}
          alt={product.name}
          className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
        />
        {product.specifications?.year && (
          <Badge className="absolute top-3 left-3 bg-primary/90 hover:bg-primary">
            {product.specifications.year}
          </Badge>
        )}
      </div>

      {/* Content Section */}
      <div className="p-5 space-y-4">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex justify-between items-start">
            <h3 className="font-semibold text-lg leading-tight">{product.name}</h3>
            <span className="text-lg font-bold text-primary">{product.price}</span>
          </div>
          {product.specifications?.mileage && (
            <p className="text-sm text-muted-foreground">
              {formatMileage(product.specifications.mileage)} miles
            </p>
          )}
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2">
          {product.description}
        </p>

        {/* Competitive Listings */}
        {product.competitiveListings && product.competitiveListings.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-1">
              Similar Listings
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Comparable vehicles from other dealers</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </h4>
            <div className="space-y-2 relative">
              {product.competitiveListings.map((listing, index) => (
                <HoverCard key={index} openDelay={100}>
                  <HoverCardTrigger asChild>
                    <a
                      href={listing.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block p-2 rounded-lg transition-colors ${
                        listing.reviews && isNegativeRating(listing.reviews.rating)
                          ? 'bg-destructive/5 hover:bg-destructive/10 border border-destructive/20'
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-16 h-16 flex-shrink-0 rounded-md overflow-hidden">
                          <img 
                            src={listing.imageUrl || product.imageUrl} 
                            alt={`${listing.dealerName} vehicle`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium truncate">{listing.dealerName}</p>
                            {listing.reviews && isNegativeRating(listing.reviews.rating) && (
                              <Badge variant="destructive" className="text-[10px] px-1.5 py-0.5 flex-shrink-0">
                                Poor Reviews
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {formatMileage(listing.mileage)} miles
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            {listing.reviews && listing.reviews.rating && (
                              <div className="flex items-center gap-2">
                                <span className="flex items-center gap-1.5">
                                  {isNegativeRating(listing.reviews.rating) ? (
                                    <ThumbsDown className="h-3 w-3 text-destructive" />
                                  ) : (
                                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                  )}
                                  <span className={`text-xs font-medium ${
                                    isNegativeRating(listing.reviews.rating) 
                                      ? 'text-destructive' 
                                      : ''
                                  }`}>
                                    {listing.reviews.rating} ({listing.reviews.totalReviews})
                                  </span>
                                </span>
                              </div>
                            )}
                            <span className="text-sm font-semibold">
                              {formatPrice(listing.price)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </a>
                  </HoverCardTrigger>
                  {listing.reviews?.review_content && listing.reviews.rating && (
                    <HoverCardContent 
                      side="top"
                      align="center"
                      className="w-80 z-50"
                      sideOffset={8}
                    >
                      <div className="space-y-2">
                        <div className="flex items-center gap-1.5">
                          {isNegativeRating(listing.reviews.rating) && (
                            <AlertCircle className="h-4 w-4 text-destructive" />
                          )}
                          <p className="font-medium">
                            Customer Reviews
                          </p>
                        </div>
                        <div className="space-y-1">
                          {listing.reviews.review_content.map((review, i) => (
                            <p key={i} className="text-sm text-destructive/90">
                              "{review}"
                            </p>
                          ))}
                        </div>
                      </div>
                    </HoverCardContent>
                  )}
                </HoverCard>
              ))}
            </div>
          </div>
        )}

        {/* Main Link */}
        <a
          href={hasValidLink ? product.link : 'javascript:void(0)'}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-2 text-sm font-medium mt-2 ${
            hasValidLink 
              ? 'text-primary hover:underline cursor-pointer' 
              : 'text-gray-400 cursor-not-allowed'
          }`}
          onClick={(e) => {
            if (!hasValidLink) {
              e.preventDefault();
              console.error('No valid link available for:', product.name);
            }
          }}
        >
          {hasValidLink ? 'View Details' : 'Link Unavailable'}
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </Card>
  );
};

export const ProductCardGrid = ({ products }: { products: Product[] }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
      {products.map((product) => (
        <ProductCard key={product.id} product={product} />
      ))}
    </div>
  );
};
